import { useEffect, useMemo, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import type { ExpressiveVoice } from '@/api/aiAgents'

type VoiceAccent = 'fr' | 'en'

interface Props {
  isOpen: boolean
  onClose: () => void
  voices: ExpressiveVoice[]
  /** '' = no voice picked yet: the default voice is preselected. */
  selectedVoiceId: string
  agentLanguage: string
  onSelectVoice: (voiceId: string) => void
}

/** Every voice speaks French and English: the accent that goes with the agent's starting language comes first. */
function groupOrder(agentLanguage: string): VoiceAccent[] {
  return agentLanguage.startsWith('en') ? ['en', 'fr'] : ['fr', 'en']
}

/**
 * Voice picker for expressive mode: only our house voices, grouped by accent,
 * each with a labeled "Listen" button when it has a sample. Phone width: a sheet
 * from the bottom with large rows; desktop: a centered dialog.
 */
export function ExpressiveVoiceModal({
  isOpen,
  onClose,
  voices,
  selectedVoiceId,
  agentLanguage,
  onSelectVoice,
}: Props) {
  const { t } = useTranslation()
  const defaultId = voices.find((v) => v.par_defaut)?.id ?? ''
  const [tempSelectedId, setTempSelectedId] = useState(selectedVoiceId || defaultId)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)

  const stopAudio = () => {
    audioRef.current?.pause()
    audioRef.current = null
    setPlayingId(null)
  }

  useEffect(() => {
    if (isOpen) {
      setTempSelectedId(selectedVoiceId || defaultId)
      return
    }
    audioRef.current?.pause()
    audioRef.current = null
    setPlayingId(null)
  }, [isOpen, selectedVoiceId, defaultId])

  useEffect(() => () => audioRef.current?.pause(), [])

  const groups = useMemo(
    () =>
      groupOrder(agentLanguage)
        .map((accent) => ({ accent, voices: voices.filter((v) => v.accent === accent) }))
        .filter((g) => g.voices.length > 0),
    [voices, agentLanguage],
  )

  const togglePlay = (e: React.MouseEvent, voice: ExpressiveVoice) => {
    e.stopPropagation()
    if (!voice.extrait) return
    if (playingId === voice.id) {
      stopAudio()
      return
    }
    audioRef.current?.pause()
    const audio = new Audio(voice.extrait)
    audioRef.current = audio
    setPlayingId(voice.id)
    audio.onended = () => setPlayingId((id) => (id === voice.id ? null : id))
    audio.play().catch(() => setPlayingId((id) => (id === voice.id ? null : id)))
  }

  const canConfirm = voices.some((v) => v.id === tempSelectedId)

  const handleConfirm = () => {
    if (!canConfirm) return
    onSelectVoice(tempSelectedId)
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className={cn(
          'flex flex-col p-0 gap-0 sm:max-w-xl max-h-[85vh]',
          // Phone width: a sheet from the bottom, full width.
          'max-sm:top-auto max-sm:bottom-0 max-sm:translate-y-0 max-sm:max-w-full max-sm:rounded-b-none max-sm:max-h-[90dvh]',
        )}
      >
        <DialogHeader className="px-4 sm:px-6 pt-5 sm:pt-6 pb-3 sm:pb-4 border-b text-left">
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            {t('manager.aiAgents.voiceModal.title')}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            {t('manager.aiAgents.expressive.modalSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-2 sm:px-4 py-2 sm:py-3">
          {groups.map((group) => (
            <section key={group.accent} className="py-1.5">
              <h3 className="px-2 pt-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                {t(`manager.aiAgents.expressive.accents.${group.accent}`)}
              </h3>
              <ul className="space-y-1">
                {group.voices.map((voice) => {
                  const isSelected = tempSelectedId === voice.id
                  const isPlaying = playingId === voice.id
                  return (
                    <li key={voice.id}>
                      <div
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onClick={() => setTempSelectedId(voice.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            setTempSelectedId(voice.id)
                          }
                        }}
                        className={cn(
                          'flex items-center gap-3 rounded-lg border px-3 py-3 sm:py-2.5 cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                          isSelected
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 dark:border-blue-500'
                            : 'border-transparent hover:bg-gray-50 dark:hover:bg-slate-800/60',
                        )}
                      >
                        <span
                          className={cn(
                            'h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors',
                            isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300 dark:border-slate-600',
                          )}
                        >
                          {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </span>
                        <span
                          aria-hidden
                          className="h-9 w-9 sm:h-8 sm:w-8 rounded-full bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200 flex items-center justify-center text-sm font-semibold flex-shrink-0"
                        >
                          {(voice.prenom || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 min-w-0">
                            <span className="font-medium text-sm text-gray-900 dark:text-white truncate">
                              {voice.prenom}
                            </span>
                            {voice.par_defaut && (
                              <span className="rounded-full bg-gray-100 dark:bg-slate-800 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:text-slate-300 flex-shrink-0">
                                {t('manager.aiAgents.expressive.defaultTag')}
                              </span>
                            )}
                          </span>
                          {voice.genre && (
                            <span className="block text-xs text-gray-500 dark:text-slate-400">
                              {t(`manager.aiAgents.expressive.genders.${voice.genre}`)}
                            </span>
                          )}
                        </span>
                        {voice.extrait && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(e) => togglePlay(e, voice)}
                            aria-label={t(
                              isPlaying ? 'manager.aiAgents.expressive.stopNamed' : 'manager.aiAgents.expressive.listenNamed',
                              { name: voice.prenom },
                            )}
                            className={cn(
                              'gap-1.5 flex-shrink-0 h-9 sm:h-8 min-w-[6.5rem]',
                              isPlaying && 'text-blue-700 border-blue-300 dark:text-blue-300 dark:border-blue-800',
                            )}
                          >
                            {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                            {t(isPlaying ? 'manager.aiAgents.expressive.stop' : 'manager.aiAgents.expressive.listen')}
                          </Button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
        </div>

        <DialogFooter className="px-4 sm:px-6 py-3 sm:py-4 border-t bg-gray-50 dark:bg-slate-900 flex-row gap-2 sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1 sm:flex-none">
            {t('manager.aiAgents.voiceModal.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white"
          >
            {t('manager.aiAgents.expressive.choose')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
